import { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget (Step-6A.2 #6).
 *
 * Graceful degradation: when ``siteKey`` is empty (the operator has not yet
 * configured TURNSTILE_SITE_KEY) the widget renders NOTHING and immediately
 * resolves with an empty token. That keeps the auth flow unblocked while the
 * defense layer waits for credentials.
 *
 * When ``siteKey`` is present, the widget loads Cloudflare's official
 * challenge script and renders the in-flow puzzle. On success it calls
 * ``onVerified(token)`` with the one-time use token to be POSTed to our
 * backend as the ``captcha_token`` field of login/signup payloads.
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

const ensureScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no window'));
    if (window.turnstile) return resolve(window.turnstile);
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = reject;
    document.head.appendChild(s);
  });

export const TurnstileWidget = ({ siteKey, onVerified, theme = 'dark' }) => {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!siteKey) {
      // Dormant — emit empty token so caller proceeds.
      onVerified?.('');
      return undefined;
    }
    ensureScript()
      .then((ts) => {
        if (!alive || !ts || !ref.current) return;
        try {
          widgetIdRef.current = ts.render(ref.current, {
            sitekey: siteKey,
            theme,
            callback: (token) => onVerified?.(token || ''),
            'error-callback': () => setErrored(true),
            'expired-callback': () => onVerified?.(''),
          });
        } catch {
          setErrored(true);
        }
      })
      .catch(() => setErrored(true));
    return () => {
      alive = false;
      if (widgetIdRef.current && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* noop */
        }
      }
    };
  }, [siteKey, onVerified, theme]);

  if (!siteKey) return null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: wrapper for the third-party Turnstile iframe; a <fieldset> would add default UA styling
    <div className="sc-turnstile-wrap" data-testid="turnstile-widget" role="group" aria-label="Human verification">
      <div ref={ref} />
      {errored && <div className="sc-turnstile-error">Verification widget failed to load. Please refresh the page.</div>}
    </div>
  );
};

export default TurnstileWidget;
